import { MarkdownTable } from '@euler/app/components/message/markdown/MarkdownTable';
import { Typography } from '@euler/app/components/typography';
import { formatMonth } from '@euler/app/flows/apdm/imports/functions/formatMonth';
import { formatPercent } from '@euler/app/flows/apdm/imports/functions/formatPercent';
import { routineCategoryLabel } from '@euler/app/flows/apdm/imports/functions/routineCategoryLabel';
import { skipReasonLabel } from '@euler/app/flows/apdm/imports/functions/skipReasonLabel';
import { ApdmImportSummary } from '@euler/functions/apdm/import/importState';
import { useResponsiveContext } from '@euler/functions/responsive/ResponsiveContext';
import { CalendarDays, Info, LucideIcon } from 'lucide-react-native';
import { ReactNode, useState } from 'react';
import { Pressable, View } from 'react-native';
import { aiColors, tint } from '../theme';

const kRoutineShown = 5;

/** What the import brings in, as its detail panel shows it. */
export function ImportDetailSummary({
  summary,
}: {
  summary: ApdmImportSummary;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ gap: rpx(16) }}>
      <Group title="来店与车辆">
        <Figure label="来店记录" value={summary.visits} />
        <Figure label="车辆" value={summary.vehicles} />
      </Group>
      <Group title="保养到期">
        <Figure label="有保养到期日" value={summary.dues.vehicles} />
        <Figure
          label="30 天内到期"
          value={summary.dues.within30Days}
          color={aiColors.primary}
          tinted
        />
        <Figure
          label="已过期"
          value={summary.dues.overdue}
          color={aiColors.red}
        />
      </Group>
      {summary.completionAssumed ? (
        <Note text="文件里没有单据状态，所有记录都按已完工导入。" />
      ) : null}
      {summary.vehicleList ? (
        <Note text="这是车辆列表：每辆车按最近一次来店计一次到店，不知道做了什么项目；同一天如有服务记录，以服务记录为准。" />
      ) : null}
      {summary.contactList ? (
        <Note text="这是客户名单：只导入了车牌，没有到店记录，可以用来给名单里的客户发消息。" />
      ) : null}
      {summary.dateRange ? (
        <IconCard icon={CalendarDays} tinted>
          <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
            记录时间
          </Typography.Text>
          <Typography.Text medium size={rpx(16)} color={aiColors.foreground}>
            {summary.dateRange.from} 至 {summary.dateRange.through}
          </Typography.Text>
        </IconCard>
      ) : null}
      <RoutineCard routine={summary.routine} />
      <MonthsCard months={summary.busiestMonths} />
      <LeftOutCard leftOut={summary.leftOut} opened={summary.detailsOpen} />
    </View>
  );
}

function Group({ title, children }: { title: string; children: ReactNode }) {
  const { rpx } = useResponsiveContext();
  return (
    <View style={{ gap: rpx(8) }}>
      <Typography.Text medium size={rpx(13)} color={aiColors.mutedForeground}>
        {title}
      </Typography.Text>
      <View style={{ flexDirection: 'row', gap: rpx(10) }}>{children}</View>
    </View>
  );
}

/** A label over its number; tinted for the one that asks for attention. */
function Figure({
  label,
  value,
  color = aiColors.foreground,
  tinted,
}: {
  label: string;
  value: number;
  color?: string;
  tinted?: boolean;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flex: 1,
        gap: rpx(6),
        paddingHorizontal: rpx(12),
        paddingVertical: rpx(12),
        borderRadius: rpx(14),
        backgroundColor: tinted ? aiColors.secondary : aiColors.muted,
      }}
    >
      <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
        {label}
      </Typography.Text>
      <Typography.Text bold size={rpx(20)} color={color}>
        {value.toLocaleString('zh-CN')}
      </Typography.Text>
    </View>
  );
}

function Card({ children }: { children: ReactNode }) {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        gap: rpx(12),
        padding: rpx(16),
        borderRadius: rpx(16),
        borderWidth: 1,
        borderColor: aiColors.border,
        backgroundColor: aiColors.card,
      }}
    >
      {children}
    </View>
  );
}

function IconCard({
  icon: Icon,
  tinted,
  children,
}: {
  icon: LucideIcon;
  tinted?: boolean;
  children: ReactNode;
}) {
  const { rpx } = useResponsiveContext();
  return (
    <View
      style={{
        flexDirection: 'row',
        alignItems: 'flex-start',
        gap: rpx(12),
        padding: rpx(14),
        borderRadius: rpx(16),
        borderWidth: 1,
        borderColor: aiColors.border,
        backgroundColor: aiColors.card,
      }}
    >
      <View
        style={{
          width: rpx(36),
          height: rpx(36),
          borderRadius: rpx(10),
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: tinted ? aiColors.secondary : aiColors.muted,
        }}
      >
        <Icon
          size={18}
          color={tinted ? aiColors.primary : aiColors.mutedForeground}
          strokeWidth={2}
        />
      </View>
      <View style={{ flex: 1, gap: rpx(2) }}>{children}</View>
    </View>
  );
}

function Note({ text }: { text: string }) {
  const { rpx } = useResponsiveContext();
  return (
    <Typography.Text size={rpx(12)} color={aiColors.mutedForeground}>
      {text}
    </Typography.Text>
  );
}

/** The maintenance the file shows most, each against the most done. */
function RoutineCard({ routine }: { routine: ApdmImportSummary['routine'] }) {
  const { rpx } = useResponsiveContext();
  const shown = routine.slice(0, kRoutineShown);
  if (!shown.length) return null;
  const most = Math.max(...shown.map(entry => entry.documents), 1);
  return (
    <Card>
      <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
        主要保养项目
      </Typography.Text>
      {shown.map(entry => (
        <View key={entry.category} style={{ gap: rpx(6) }}>
          <View
            style={{ flexDirection: 'row', justifyContent: 'space-between' }}
          >
            <Typography.Text medium size={rpx(13)} color={aiColors.foreground}>
              {routineCategoryLabel(entry.category)}
            </Typography.Text>
            <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
              {entry.documents.toLocaleString('zh-CN')} 单
            </Typography.Text>
          </View>
          <View
            style={{
              height: rpx(6),
              borderRadius: rpx(3),
              backgroundColor: aiColors.muted,
              overflow: 'hidden',
            }}
          >
            <View
              style={{
                width: `${(entry.documents / most) * 100}%`,
                height: '100%',
                borderRadius: rpx(3),
                backgroundColor: aiColors.primary,
              }}
            />
          </View>
        </View>
      ))}
    </Card>
  );
}

function MonthsCard({
  months,
}: {
  months: ApdmImportSummary['busiestMonths'];
}) {
  const { rpx } = useResponsiveContext();
  if (!months.length) return null;
  return (
    <Card>
      <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
        来店最多的月份
      </Typography.Text>
      {months.map((entry, index) => (
        <View
          key={entry.month}
          style={{
            flexDirection: 'row',
            alignItems: 'center',
            gap: rpx(10),
            paddingTop: index ? rpx(12) : 0,
            borderTopWidth: index ? 1 : 0,
            borderTopColor: aiColors.border,
          }}
        >
          <View
            style={{
              width: rpx(22),
              height: rpx(22),
              borderRadius: rpx(11),
              alignItems: 'center',
              justifyContent: 'center',
              backgroundColor: index
                ? tint(aiColors.primary, 0.1)
                : aiColors.primary,
            }}
          >
            <Typography.Text
              medium
              size={rpx(11)}
              color={index ? aiColors.primary : aiColors.primaryForeground}
            >
              {index + 1}
            </Typography.Text>
          </View>
          <Typography.Text
            size={rpx(14)}
            color={aiColors.foreground}
            style={{ flex: 1 }}
          >
            {formatMonth(entry.month)}
          </Typography.Text>
          <Typography.Text medium size={rpx(14)} color={aiColors.foreground}>
            {entry.visits.toLocaleString('zh-CN')} 次
          </Typography.Text>
        </View>
      ))}
    </Card>
  );
}

/** What was left out, folded unless too much was. */
function LeftOutCard({
  leftOut,
  opened,
}: {
  leftOut: ApdmImportSummary['leftOut'];
  opened: boolean;
}) {
  const { rpx } = useResponsiveContext();
  const [expanded, setExpanded] = useState(opened);
  const line = [
    leftOut.rows
      ? `${leftOut.rows} 行未导入（${formatPercent(
          leftOut.rows,
          leftOut.totalRows,
        )}）`
      : '所有行都已导入',
    leftOut.unclassifiedDocuments
      ? `${leftOut.unclassifiedDocuments} 单含未识别的项目，只计来店不计保养`
      : null,
  ]
    .filter((part): part is string => part !== null)
    .join('；');
  const reasons: string[][] = [
    ...leftOut.byReason.map(entry => [
      skipReasonLabel(entry.reason),
      `${entry.rows} 行`,
      '未导入',
    ]),
    ...(leftOut.notCompleted
      ? [['未完工或已作废', `${leftOut.notCompleted} 行`, '不计来店']]
      : []),
    ...(leftOut.washOnly
      ? [['只洗车', `${leftOut.washOnly} 单`, '不计来店也不计保养']]
      : []),
  ];
  const hasDetails = reasons.length > 0 || leftOut.topUnknownItems.length > 0;
  return (
    <IconCard icon={Info}>
      <Typography.Text medium size={rpx(15)} color={aiColors.foreground}>
        未导入的内容
      </Typography.Text>
      <Typography.Text size={rpx(13)} color={aiColors.mutedForeground}>
        {line}
      </Typography.Text>
      {expanded ? (
        <View style={{ marginTop: rpx(6) }}>
          {reasons.length ? (
            <MarkdownTable
              rows={[['原因', '数量', '说明'], ...reasons]}
              align={['left', 'right', 'left']}
            />
          ) : null}
          {leftOut.topUnknownItems.length ? (
            <MarkdownTable
              rows={[
                ['未识别的项目', '次数'],
                ...leftOut.topUnknownItems.map(item => [
                  item.text,
                  `${item.count}`,
                ]),
              ]}
              align={['left', 'right']}
            />
          ) : null}
        </View>
      ) : null}
      {hasDetails ? (
        <Pressable
          accessibilityRole="button"
          accessibilityState={{ expanded }}
          hitSlop={6}
          onPress={() => setExpanded(value => !value)}
          style={{ marginTop: rpx(4) }}
        >
          <Typography.Text medium size={rpx(13)} color={aiColors.primary}>
            {expanded ? '收起详情' : '查看详情'}
          </Typography.Text>
        </Pressable>
      ) : null}
    </IconCard>
  );
}
